<script setup lang="ts">
import { onUnmounted, ref, toRefs, watch, onMounted, computed } from 'vue';
import { useCustomerMgmt } from '@ilteducation/customer-mgmt';
import type { FetchListOptions, PaginationOptions } from '@ilteducation/customer-mgmt';
import { VDataTableServer } from 'vuetify/components';
import { useDisplay } from 'vuetify';
import { VTextField } from 'vuetify/components/VTextField';
import useLocale from './helpers/use-locale';

type ReadonlyHeaders = Partial<VDataTableServer['headers']> & { key: string }[];

type Emits = {
  (e: 'update:modelValue', selectedGroups: string[]): void;
};
const emit = defineEmits<Emits>();

type Props = {
  modelValue: string[]; // selected
  schoolUnitId: string;
  isFetchingSchoolUnits: boolean;
  forceSmaller?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  isFetchingSchoolUnits: false,
  forceSmaller: undefined,
});
const { t } = useLocale();
const { smAndDown } = useDisplay();

const defaultOptionsGroups = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['name'],
  sortDesc: [false],
  groupBy: ['id'],
  groupDesc: [false],
  mustSort: false,
  multiSort: false,
  searchString: '',
  filters: {},
  force: false,
} as FetchListOptions;

const { schoolUnitId, isFetchingSchoolUnits, forceSmaller } = toRefs(props);
const optionsGroups = ref(defaultOptionsGroups);
const searchString = ref<string>('');
const searchInputRef = ref<VTextField>();

const isSmAndDown = computed(() => forceSmaller.value ?? smAndDown.value);

const {
  displayedItems: groups,
  isFetching: isFetchingGroups,
  totalItems: totalGroups,
  setSearchField: setSearchFieldGroups,
  setFetchParams: setFetchParamsGroups,
  cleanAndFetch: cleanAndFetchGroups,
  resetToDefaults: resetToDefaultGroups,
} = useCustomerMgmt({ context: 'groups', options: defaultOptionsGroups });

const searchGroup = () => {
  if (searchInputRef.value) {
    searchInputRef.value.blur();
  }
  optionsGroups.value.searchString = searchString.value;
  setSearchFieldGroups(searchString.value);
  cleanAndFetchGroups();
};

const loadGroupsOnPageChange = (page: PaginationOptions['page']) => {
  optionsGroups.value.page = page;
  setFetchParamsGroups({ ...optionsGroups.value });
  if (schoolUnitId.value) {
    cleanAndFetchGroups();
  }
};
const loadGroupsOnItemsPerPageChange = (itemsPerPage: PaginationOptions['itemsPerPage']) => {
  optionsGroups.value.itemsPerPage = itemsPerPage;
  setFetchParamsGroups({ ...optionsGroups.value });
  if (schoolUnitId.value) {
    cleanAndFetchGroups();
  }
};
const loadGroupsOnSortByChange = (sortBy: PaginationOptions['sortBy']) => {
  if (sortBy.length > 0) {
    optionsGroups.value.sortBy = [sortBy[0].key];
    optionsGroups.value.sortDesc = [sortBy[0].order !== 'asc'];
  }
  setFetchParamsGroups({ ...optionsGroups.value });
  if (schoolUnitId.value) {
    cleanAndFetchGroups();
  }
};

watch(schoolUnitId, async () => {
  optionsGroups.value.filters = { ...optionsGroups.value.filters, ...{ schoolUnits: [schoolUnitId.value] } };

  resetToDefaultGroups();
  setFetchParamsGroups(optionsGroups.value);
  if (!isFetchingGroups.value) {
    await cleanAndFetchGroups();
  }
});

const updateModel = (selectedGroups: string[]) => {
  emit('update:modelValue', selectedGroups);
};

const doSelectAll = (allSelected: boolean, defaultSelectAll: (allSelected: boolean) => void) => {
  const maxSelectedItems = 120;
  if (allSelected) {
    updateModel([]);
    return;
  }

  if (props.modelValue.length > maxSelectedItems) {
    const selected = props.modelValue.slice(0, maxSelectedItems).map((u) => u);
    updateModel(selected);
    return;
  }
  defaultSelectAll(!allSelected);
};

onUnmounted(() => {
  optionsGroups.value = defaultOptionsGroups;
  resetToDefaultGroups();
  setFetchParamsGroups(optionsGroups.value);
});

const tableHeaders = computed(() =>
  [
    { title: 'components.name', key: 'name', value: 'name', hide: false },
    { title: 'components.members', key: 'memberCount', value: 'memberCount', hide: isSmAndDown.value },
    {
      key: 'data-table-select',
      align: 'center',
      width: '150px',
      hide: false,
    },
  ].filter((column) => !column.hide),
);

onMounted(async () => {
  optionsGroups.value.filters = { ...optionsGroups.value.filters, ...{ schoolUnits: [schoolUnitId.value] } };

  resetToDefaultGroups();
  setFetchParamsGroups(optionsGroups.value);
  if (!isFetchingGroups.value) {
    await cleanAndFetchGroups();
  }
});
</script>

<template>
  <div>
    <v-text-field
      ref="searchInputRef"
      v-model="searchString"
      type="search"
      class="mx-4"
      :label="t('components.input_search')"
      prepend-inner-icon="mdi-magnify"
      :single-line="true"
      hide-details
      variant="solo"
      :clearable="true"
      density="compact"
      rounded="lg"
      flat
      @keyup.enter="searchGroup"
      @click:clear="searchGroup"
    />
    <v-card-text>
      <v-data-table-server
        :model-value="modelValue"
        :headers="tableHeaders as ReadonlyHeaders"
        :items="groups"
        :loading="isFetchingGroups || isFetchingSchoolUnits"
        :items-per-page="optionsGroups.itemsPerPage"
        :items-length="totalGroups ?? 0"
        :multi-sort="false"
        :loading-text="t('components.loading_groups')"
        :no-data-text="t('components.no_data_available')"
        :items-per-page-text="t('components.items_per_page')"
        :next-page-label="t('components.next_page')"
        :page-text="`{0} - {1} ${t('components.of')} {2}`"
        :prev-page-label="t('components.prev_page')"
        :last-page-label="t('components.last_page')"
        :items-per-page-options="[
          { value: 10, title: '10' },
          { value: 15, title: '15' },
          { value: 30, title: '30' },
        ]"
        show-select
        item-key="uid"
        item-value="uid"
        class="data-table"
        @update:page="loadGroupsOnPageChange"
        @update:itemsPerPage="loadGroupsOnItemsPerPageChange"
        @update:sortBy="loadGroupsOnSortByChange"
        @update:modelValue="updateModel"
      >
        <template #[`headers`]="{ columns, selectAll, allSelected }">
          <tr>
            <template v-for="column in columns" :key="column.key">
              <th
                v-if="column.key === 'data-table-select'"
                class="bg-transparent mr-2"
                scope="col"
                :class="`text-${column.align}`"
                :style="`min-width: ${column.width}`"
              >
                <v-btn
                  class="pa-0 px-1 rounded-lg"
                  :disabled="isFetchingGroups"
                  variant="text"
                  @click="doSelectAll(allSelected, selectAll)"
                >
                  <span class="text-none select-all">
                    {{ allSelected ? t('components.deselect_all') : t('components.select_all') }}
                  </span>
                </v-btn>
              </th>
              <th
                v-else
                class="bg-transparent mr-2"
                scope="col"
                :class="`text-${column.align}`"
                :style="`width: ${column.width}`"
              >
                {{ t(column.title ?? '') }}
              </th>
            </template>
          </tr>
        </template>
      </v-data-table-server>
    </v-card-text>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.data-table {
  border-radius: 10px;
  height: 100%;
}
:deep(.v-table__wrapper) {
  overflow: visible;
}
:deep(.v-selection-control) {
  justify-content: center;
}
.select-all {
  text-wrap: wrap;
}
@media #{map.get(v.$display-breakpoints, 'xs')} {
  :deep(.v-data-table__td) {
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 70px;
    overflow: hidden;
  }
}
</style>
